
import Config from 'app-config.json';
import TKCache from './cache';
import { logout, setUser } from './auth';
import Util from './util';

export class ResourceNotFoundError extends Error
{
  constructor()
  {
    super('Resource is not found.')
  }
}

export const G_apiBaseUrl = (
  ('true'===process.env.REACT_APP_ENVPROD)
    ? `${window.location.hostname}:${window.location.port}/${Config.api.base_url}`
    : `${Config.api.host}:${Config.api.port}/${Config.api.base_url}/`
  ).replace(/(^\/|\/{2,})/g, '/');

let jwtToken = window.localStorage.getItem('security-token');
let apiopt = { async: true };

export function setToken(token) { window.localStorage.setItem('security-token', jwtToken=token) }

function fetchApi(method, path, body, headers_in, nonJson)
{
  const base  = G_apiBaseUrl;
  const mode  = 'cors';
  const cache = 'no-cache';
  const headers = Object.assign({'Content-Type': 'application/json'}, headers_in);

  if (jwtToken) headers.Authorization = `Bearer ${jwtToken}`;

  path = `${window.location.protocol}//${`${base}/${path}`.replace(/(^\/|\/{2,})/g, '/')}`;
  if (body) body = Util.toJson(body);
  let opt = {method, headers, body, mode, cache};
  Object.assign(opt, apiopt);
  return fetch(path, opt)
    .catch(reason => {
      console.error(reason.message);
    })
    .then( async (resp) =>
    {
      if (!resp) return { status: 0, success: false, error: '無法連接伺服器。' };
      switch (resp.status) {
        case 500:
        case 400: return { status: resp.status, success: false, error: await resp.text() };
        case 401: return logout(window.location.href = "/login");
        case 403: window.location.href = "/"; return;
        case 404: throw new ResourceNotFoundError();
      }
      if (nonJson) return resp;
      if (200 == resp.status) return await resp.json();
      return resp;
    });
}

export function getApi(path, nonJson) { return fetchApi('GET', path, null, null, nonJson||false) }
export function postApi(path, body, nonJson) { return fetchApi('POST', path, body, null, nonJson||false) }
function putApi(path, body) { return fetchApi('PUT', path, body) }
function deleteApi(path, body) { return fetchApi('DELETE', path, body) }

class ApiController
{
  sync() { apiopt.async = false; }
  async() { apiopt.async = true; }

  //----------------------------------------------------------
  // launch profile functions
  getAppVersion() { return getApi('info/version') }
  async validateToken()
  {
    let info = await getApi('validate-token');
    if (0 === info.status) {
      logout();
      return false;
    } else {
      setToken(info.token);
      setUser(info.name, info.role);
      return true;
    }
  }
  getCompanyInfo() { return getApi('info/tk-info') }
  getHomeData() { return getApi('home') }
  getConfig() { return getApi('config') }

  async getPaymentReceiptInfo(payment)
  {
    // construct payment ID
    let uid = '00000000' + payment.uid;
    let seq = '0' + payment.useq;
    uid = uid.substring(uid.length - 8);
    seq = seq.substring(seq.length - 2);

    // construct full path
    let prev  = `${payment.company}IV-${payment.ix_yrmo}`
    let path  = `/op/${prev}-${uid}-${seq}.jpg`;

    let img = await getApi(path, true).then(resp => {
      return resp.text();
    });
    return {
       payment_src: img
      ,payment_id: `${prev}-${uid}${seq}`
    };
  }

  //----------------------------------------------------------
  // start of print config
  editPrintConfig(config) { return postApi('config', { print: config }) }

  //----------------------------------------------------------
  // start of customers
  getCustomerList() { return getApi('info/customers') }
  getCustomerDetail(uid) { return getApi(`info/customers/i/${uid}`) }
  findCustomer(license, owner_id) { return getApi(`info/customers/v/${license}` + (owner_id ? `/${owner_id}` : '')) }
  findContactOwner(contact) { return getApi(`info/customers/c/${contact}`) }
  newCustomer(customer, vehicle, service) { return postApi('op/new-customer', { customer, vehicle, service }) }
  editCustomer(c, v)
  {
    let customer = {...c};
    let vehicle  = {...v};
    delete customer.vehicles;
    delete vehicle.services;
    return putApi(`info/customers/${customer.id}`, { customer, vehicle });
  }

  //----------------------------------------------------------
  // start of staff
  getStaffList() { return getApi('info/staff') }
  getWorkerList() { return getApi('info/worker') }
  addStaff(staff) { return postApi('register', staff) }
  editStaff(staff) { return putApi(`info/staff/${staff.id}`, staff) }
  changePassword(dto) { return putApi('change-password', dto) }

  //----------------------------------------------------------
  // start of vehicle
  addVehicle(vehicle) { return postApi(`info/vehicle/${vehicle.owner_id}`, vehicle) }
  editVehicle(vehicle) { return putApi(`info/vehicle/${vehicle.owner_id}/${vehicle.cd_license}`, vehicle) }
  deleteVehicle(vehicle) { return deleteApi(`info/vehicle/${vehicle.owner_id}/${vehicle.cd_license}`, vehicle) }

  //----------------------------------------------------------
  // start of locations
  getServiceLocationList() { return getApi('info/locations') }
  addLocation(location) { return postApi('info/location', location) }
  editLocation(location) { return putApi('info/location', location) }

  //----------------------------------------------------------
  // service plan
  getServicePlanList() { return getApi('info/service-plans') }
  getServicePlanDetail(plan) { return getApi(`info/service-plans/${plan.company}/${plan.srv_dis}-${plan.srv_loc}/${plan.cd_plan}-${plan.vh_type}`) }
  editPlan(plan) { return putApi('info/service-plan', plan) }

  //----------------------------------------------------------
  // start of pricing
  editPlanPriceBatch(dto) { return putApi('price/batch/plan', dto) }
  editServicePriceBatch(dto) { return putApi('price/batch/service', dto) }
  sendPriceChangeMessage(dto) { return postApi('announcement/price-change', dto) }

  //----------------------------------------------------------
  // start of services
  getVhServicesByDate(dto) { return postApi('info/vehicle-service', dto) }

  changeServiceRecord(dto) { return postApi(`op/v/${dto.cd_license}/service`, dto) }
  editServiceRecord(dto) { return putApi(`op/v/${dto.cd_license}/service`, dto) }
  deleteServiceRecord(service) { return deleteApi('op/service', service) }
  refreshServiceRecord(dto) { return putApi(`op/v/${dto.vehicle.cd_license}/all-services`, dto) }

  getUnitServices() { return getApi('info/unit-service') }
  addUnitService(dto) { return postApi('info/unit-service', dto) }
  editUnitService(dto) { return putApi('info/unit-service', dto) }

  //----------------------------------------------------------
  // start of orders
  getOrdersByCustomer(uid) { return getApi('op/order' + (!uid?'':'/u'+uid)) }
  getOrdersByFilter(dto) { return postApi('op/order/d', dto) }
  placeOrder(order) { return postApi('op/order', order) }
  editOrder(order) { return putApi(`op/order`, order) }
  deleteOrder(order) { return deleteApi(`op/order`, order) }

  editOrderItem(items) { return putApi(`op/order/items`, items) }

  //----------------------------------------------------------
  // get service suspension history
  getSuspHistByLicense(license) { return getApi(`info/susp-history/v/${license}`) }
  getSuspHistByDate(dto) { return postApi(`info/susp-history/d`, dto) }
  deleteSuspensionHistory(hist) { return deleteApi(`info/susp-history`, hist) }

  //----------------------------------------------------------
  // get license change records
  getLicenseChangeRecord(owner_id) { return getApi(`info/vehicles/o${owner_id}-lcr`) }

  //----------------------------------------------------------
  // bills
  getBills(owner_id) { return getApi(`op/u${owner_id}/bill`) }
  getOverdueBills() { return getApi(`op/bill/overdue`) }
  recalcCustBills(uid, ix, dto) { return putApi(`op/bill/u${uid}/${ix}/regen`, dto) }
  recalcAllBills(ix) { return putApi(`op/bill/all/${ix}/regen`) }
  generateBills(dto)
  {
    if (dto)
      return putApi(`admin/gen-bills/${dto.license}`, dto);
    else
      return putApi(`admin/gen-bills`);
  }
  checkBills(owner_id) { return putApi(`op/bill/u${owner_id}/check`) }
  editDiscount(dto) { return putApi(`op/bill/discount`, dto) }

  deleteOutstandingBills(ix) { return deleteApi(`op/bill/${ix}`) }

  //----------------------------------------------------------
  // payment
  getPaymentByMonth(by_date, ix) { return getApi(`op/payments/${by_date}/${ix}`) }
  getPaymentByDate(by_date, from_dtm, to_dtm) { return getApi(`op/payments/from/${by_date}/${Util.ShortDate(from_dtm)}/to/${Util.ShortDate(to_dtm)}`) }
  getPaymentByChequeRef(ref) { return getApi(`op/payments/ref/${ref}`) }
  acceptPayment(dto) { return postApi(`op/u${dto.uid}/inv`, dto) }
  editPayment(iv) { return putApi(`op/payment`, iv) }
  deletePayment(iv) { return deleteApi(`op/payment`, iv) }

  //----------------------------------------------------------
  // e-Invoicing
  sendInvoice(list) { return postApi(`op/e-invoice/send`, list) }
  sendReceipt(list) { return postApi(`op/e-receipt/send`, list) }

  getEInvoicingAudit(dto) { return postApi(`op/e-invoicing/audit`, dto) }

  //----------------------------------------------------------
  // schedule
  getWorkerSchedule(worker_id, ix) { return getApi(`schedule/w${worker_id}/${ix}`) } // one month (yyyyMM)
  getLocationSchedule(district, location) { return getApi(`schedule/dist/${district}-${location}`) } // -6mo +3mo
  getStaffLeave(staff_id) { return getApi(`schedule/leave/${staff_id}`) }  // -3mo +1mo
  applyLeave(dto) { return postApi(`schedule/leave`, dto) }
  deleteLeave(dto) { return deleteApi(`schedule/leave`, dto) }

  //----------------------------------------------------------
  // report
  getReportProfit() { return getApi(`report/income`) }        // -12mo
  getReportLocation(filter) { return postApi(`report/location`, filter) }
  getStaffSalary(ix) { return getApi(`report/salary/${ix}`) }
  getLHSReport(ix) { return getApi(`report/lhs-${ix}`) }      // -6wk

  //----------------------------------------------------------
  // management
  searchCustomer(dto) { return postApi(`info/customers/search`, dto) }
  getServingWorkers(loc) { return getApi(`mgt/serving-worker/${loc.srv_dis}/${loc.srv_loc}`) }
  switchWorker(dto) { return putApi(`mgt/serving-worker`, dto) }
  mergeCustomers(ids) { return putApi(`mgt/merge-customer`, ids) }
  splitCustomer(sv) { return putApi(`mgt/split-customer`, sv) }

  //----------------------------------------------------------
  // instant messaging
  getLatestMessages() { return getApi(`im/latest-messages`) }
  getMessages(wa_id, maxseq) { return getApi(`im/messages/${wa_id}/${maxseq}`) }
  getMessageMedia(wa_id, seq) { return getApi(`im/media/${wa_id}/${seq}`, true) }

  //----------------------------------------------------------
  // export file
  exportClientList(filter) { return postApi(`export/client-list`, filter, true) }
  exportWorkerSchedule(worker_id, ix) { return getApi(`export/schedule/w${worker_id}/${ix}`, true) }
  exportLocationSchedule(dis, loc, date) { return getApi(`export/schedule/dist/${dis}-${loc}_${Util.ShortDate(date)}`, true) }
  exportLocationScheduleAll(dis, loc) { return getApi(`export/schedule/dist/${dis}-${loc}-all`, true) }
  exportOverdueBills() { return getApi(`export/overdue-bills`, true) }
  exportStaffList() { return getApi(`export/staff`, true) }
  exportLocationList() { return getApi(`export/service-locations`, true) }
  exportServicePlan() { return getApi(`export/service-plans`, true) }
  exportLHSReport(ix) { return getApi(`export/lhs-report/${ix}`, true) }
  export3DistLocationReport(filter) { return postApi(`export/location-report-3`, filter, true) }
  export7DistLocationReport(filter) { return postApi(`export/location-report-7`, filter, true) }
  exportStaffSalary(ix, id) { return getApi(`export/salary/${ix}${id?'/'+id:''}`, true) }
  exportServiceRecord(filter) { return postApi(`export/service-records`, filter, true) }
  exportSuspensionRecord(filter) { return postApi(`export/suspension-records`, filter, true) }
  exportPaymentRec(by_date, ix, filter) { return postApi(`export/payments/${by_date}/${ix}`, filter, true) }
  exportOrder(filter) { return postApi(`export/orders`, filter, true) }

  //----------------------------------------------------------
  get Token() { return jwtToken; }

}

//---------------------------------------------------------------------------
class SysController
{
  editBalance(dto) { return putApi(`admin/balance`, dto) }
  deleteBill(dto) { return deleteApi(`admin/bill`, dto) }
}

//---------------------------------------------------------------------------
const API = new ApiController();
const SYS = new SysController();
let ApiReady;

if (jwtToken) {
  ApiReady = Promise.all([
    API.validateToken(),
    API.getCompanyInfo().then(data => {
      TKCache.Translate = data.translate;
      delete data.translate;
      Object.freeze(window.DATA = data);
    }),
    API.getConfig().then(data => {
      Cache.Config = data;
    }),
  ]);
} else {
  ApiReady = (async () => false)();
}

export { ApiReady, SYS, API };
export default API;
